import {IXAddr, XA} from "lib/commons/xml/xAddr";
import {XmlDeleteMsg, XmlHouse, XmlInsertMsg, XmlStrMsg} from "lib/edit/ot/xmlHouse";
import {Action} from "lib/commons/actions";
import {IRegPointer, IUiEnv} from "lib/commons/registry";
import {InitSlaveRep, Msg, ResetStatesMsg, State} from "lib/edit/ot/urban";
import {JML} from "lib/commons/xml/jml";
import {isMsgUpdater} from "lib/edit/ot/houseOt";
import {POPUP} from "back/commons/widgets/popups";


/**
 * Gestionnaire de l'historique d'édition inter-éditeurs.
 */
export class HistoEditPointsMgr {

	static maxStackSize = 15;
	static clearStackStep = 5;

	protected stack: HistoEditEntry[] = [];

	/**
	 * Offset en partant de la fin de la stack indiquant l'entrée d'édition courante.
	 */
	protected currentEntry: number = 0;

	/**
	 * Ajoute une entrée d'historique.
	 * Si la nouvelle entrée est dans le même sameCtx (même targetAddr de 0 à sameCtxDepth ET la même fct goto) que la dernère entrée d'édition,
	 * alors la target est remplacée, sinon une entrée est ajoutée.
	 *
	 * Lorsque ce targetAddr n'est plus référencé par ce HistoEdit, HistoEditPoint.kill() sera appelé pour être
	 * purgé de la house et ne plus être ajusté par les éditions concurrentes.
	 *
	 * @param goto function qui permet de repointer une entrée d'historique. Cette function externe permet de gérer le (ré)affichage
	 * de l'éditeur cible d'une part et consitue d'autre part un identifiant de l'éditeur cible : 2 entrées d'édition ne peuvent
	 * être fusionnées que si leurs 2 function goto sont strictement identiques (===).
	 */
	addEditEntry(targetAddr: HistoEditPoint, sameCtxDepth: number | null, editHolder: IHistoEditHolder) {
		if (sameCtxDepth == null) sameCtxDepth = targetAddr.addr.length;
		this.currentEntry = 1; //toute edition annule la remontée de la stack et se place sur la dernière entrée
		let last: HistoEditEntry | undefined;
		let i = this.stack.length;
		while (--i >= 0) {
			const entry = this.stack[i];
			if (!entry.targetAddr.killed) {
				last = entry;
				break;
			}
		}
		if (last?.isSameCtx(targetAddr, sameCtxDepth, editHolder)) {
			last.targetAddr.kill();
			last.targetAddr = targetAddr;
		} else {
			this.stack.push(new HistoEditEntry(targetAddr, sameCtxDepth, editHolder));
			if (this.stack.length > HistoEditPointsMgr.maxStackSize) {
				for (let i = 0; i < HistoEditPointsMgr.clearStackStep; i++) {
					this.stack[i].targetAddr.kill();
				}
				this.stack.splice(0, HistoEditPointsMgr.clearStackStep);
			}
		}
	}

	/** Retourne à l'édition précédente */
	gotoPreviousEdit() {
		let i = this.stack.length - this.currentEntry;
		while (--i >= 0) {
			const entry = this.stack[i];
			if (!entry.targetAddr.killed) {
				if (entry.editHolder.goTo(entry.targetAddr)) {
					this.currentEntry = this.stack.length - i;
					return;
				}
			} else {
				//entrée déjà killed (suppr d'un contexte parent), on purge
				this.stack.splice(i, 1);
			}
		}
	}

	/**
	 * Informe d'un chgt de focus.
	 * Si focusXa est dans le sameCtx (même targetAddr de 0 à sameCtxDepth ET la même fct goto)
	 * par rapport la dernière entrée d'édition,
	 * gotoPreviousEdit() repointera la précédente entrée d'édition (pas d'effets),
	 * sinon le curseur de l'édition en cours est reseté et gotoPreviousEdit() repointera la dernière édition en date.
	 *
	 */
	onEditPointFocus(focusXa: IXAddr | null, editHolder: IHistoEditHolder) {
		const last = this.stack[this.stack.length - this.currentEntry];
		if (last?.isSameCtxFocus(focusXa, editHolder)) {
			//on focus dans le point d'édition courant, on ne change rien.
		} else {
			//on se place après la dernière entrée édition pour la repointer si gotoPreviousEdit() est appelé
			this.currentEntry = 0;
		}
	}

	/** A appeler sur la perte de focus d'un éditeur pour reset le point d'édition courant, et redémarrer à 0. */
	onEditorLooseFocus(editHolder: IHistoEditHolder) {
		//on se place après la dernière entrée édition pour la repointer si gotoPreviousEdit() est appelé
		const last = this.stack[this.stack.length - this.currentEntry];
		if (last?.editHolder.isSame(editHolder)) this.currentEntry = 0;
	}

}

class HistoEditEntry {
	constructor(public targetAddr: HistoEditPoint, public sameCtxDepth: number, public editHolder: IHistoEditHolder) {}

	isSameCtx(targetAddr: HistoEditPoint, sameCtxDepth: number, editHolder: IHistoEditHolder): boolean {
		if (this.targetAddr.killed || !editHolder.isSame(this.editHolder) || sameCtxDepth !== this.sameCtxDepth) return false;
		for (let i = 0; i < sameCtxDepth; i++) {
			if (targetAddr.addr[i] !== this.targetAddr.addr[i]) return false;
		}
		return true;
	}

	isSameCtxFocus(focusXa: IXAddr, editHolder: IHistoEditHolder): boolean {
		if (this.targetAddr.killed || !editHolder.isSame(this.editHolder) || focusXa.length !== this.sameCtxDepth) return false;
		for (let i = 0; i < this.sameCtxDepth; i++) {
			if (focusXa[i] !== this.targetAddr.addr[i]) return false;
		}
		return true;
	}
}

/**
 * Contexte d'éditeur permettant
 * - d'identifier si on est dans le même contexte d'édition,
 * - de (ré)afficher un point d'édition précédent.
 */
export interface IHistoEditHolder {
	isSame(other: IHistoEditHolder): boolean

	goTo(targetAddr: HistoEditPoint): boolean
}

/** Action à insérer dans l'ihm. */
export class HistoEditAction extends Action<IRegPointer<IUiEnv>> {

	public histoEditPoints: HistoEditPointsMgr;

	constructor(histoEditPoints: HistoEditPointsMgr, id?: string) {
		super(id);
		this.histoEditPoints = histoEditPoints;
		this._icon = "/@skin@/edit/wed/histoEdit.svg";
		this._label = "Retourner au précédent point d'édition";
	}

	isInstantiable(ctx: IRegPointer<IUiEnv>): boolean {
		if (ctx instanceof Element) return POPUP.findPopupableParent(ctx) == null; //Pas si on est dans un dialogue
		return true;
	}

	execute(ctx: IRegPointer<IUiEnv>, ev?: Event): any {
		this.histoEditPoints.gotoPreviousEdit();
	}
}

export interface IHistoEditEnv {
	histoEditMgr?: HistoEditPointsMgr
}

export function isHistoEditEnv(env: any): env is IHistoEditEnv {return env?.histoEditMgr instanceof HistoEditPointsMgr}

/**
 * Point d'édition constituant les entrées de l'historique.
 */
export class HistoEditPoint {

	addr: IXAddr | null;
	state: HistoEditPointsState;

	constructor(addr: IXAddr, house: XmlHouse) {
		this.addr = XA.freeze(addr);
		let state = house.getStateById(HistoEditPointsState.stateId) as HistoEditPointsState;
		if (state == null) {
			state = new HistoEditPointsState();
			house.putState(state);
		}
		this.state = state
		state.points.add(this);
	}

	update(addr: IXAddr | null) {
		if (addr == null) this.kill();
		else this.addr = addr;
	}

	get killed() {return this.addr == null}

	kill() {
		this.addr = null;
		this.state.points.delete(this);
	}
}

class HistoEditPointsState extends State {

	static stateId = "histoEditPoints";

	points: Set<HistoEditPoint> = new Set();

	// @ts-ignore
	get id() {return HistoEditPointsState.stateId}

	update(msg: Msg) {
		switch (msg.type) {
		case XmlInsertMsg.type : {
			const m = msg as XmlInsertMsg;
			if (m.killed) return;
			for (let point of this.points) point.update(XA.freeze(XA.translateInsSeq(point.addr, m.xa, JML.lengthJmlOrText(m.jml))));
			break;
		}
		case XmlDeleteMsg.type : {
			const m = msg as XmlDeleteMsg;
			if (m.killed) return;
			for (let point of this.points) point.update(XA.freeze(XA.translateDelSeq(point.addr, m.xa, m.len, true)));
			break;
		}
		case XmlStrMsg.type : {
			const m = msg as XmlStrMsg;
			if (m.killed) return;
			for (let point of this.points) if (XA.isAnc(m.xa, point.addr)) point.kill();
			break;
		}
		case ResetStatesMsg.type :
		case InitSlaveRep.type :
			for (let point of this.points) point.kill();
			break;
		default:
			if (isMsgUpdater(msg)) msg.applyUpdates(this);
		}
	}
}
